import type { ClinicGoal } from '@services/api/dashboard/clinic-goal.api'

/** Model fields the dashboard reads and that are editable from Settings → Goals. */
export type ClinicGoalField = keyof Omit<
  ClinicGoal,
  | 'clinic_goal_id'
  | 'clinic_id'
  | 'period'
  | 'date_time_created_at'
  | 'date_time_updated_at'
>

export type GoalFormat = 'currency' | 'percent' | 'int' | 'days'

export interface GoalMetric {
  /** Unique row id — matches the backend model field name. */
  id: ClinicGoalField
  field: ClinicGoalField
  metricType: string
  format: GoalFormat
}

export interface GoalSection {
  id: string
  title: string
  metrics: GoalMetric[]
}

const metric = (
  field: ClinicGoalField,
  metricType: string,
  format: GoalFormat,
): GoalMetric => ({ id: field, field, metricType, format })

export const GOALS_SECTIONS: GoalSection[] = [
  {
    id: 'production-financial',
    title: 'Production & financial',
    metrics: [
      metric('gross_production_goal', 'Gross production', 'currency'),
      metric('net_production_goal', 'Net production', 'currency'),
      metric(
        'gross_production_chart_goal',
        'Production chart target',
        'currency',
      ),
      metric('gross_collection_goal', 'Gross collections', 'currency'),
      metric(
        'gross_collection_chart_goal',
        'Collections chart target',
        'currency',
      ),
      metric('collection_header_goal', 'Total collections', 'currency'),
      metric('collection_chart_goal', 'Collections chart target', 'currency'),
      metric('collection_rate_target_pct', 'Collection rate', 'percent'),
      metric('net_profit_goal', 'Net profit', 'currency'),
      metric(
        'practice_gross_collection_goal',
        'Practice gross collection',
        'currency',
      ),
      metric(
        'practice_net_collection_goal',
        'Practice net collection',
        'currency',
      ),
      metric(
        'practice_revenue_trend_goal',
        'Practice revenue trend',
        'currency',
      ),
    ],
  },
  {
    id: 'collections',
    title: 'Collections',
    metrics: [
      metric(
        'patient_collection_dollar_goal',
        'Patient collections',
        'currency',
      ),
      metric(
        'insurance_collection_dollar_goal',
        'Insurance collections',
        'currency',
      ),
      metric(
        'patient_collection_share_goal_pct',
        'Patient collection share',
        'percent',
      ),
      metric(
        'insurance_collection_share_goal_pct',
        'Insurance collection share',
        'percent',
      ),
      metric(
        'patient_days_to_collect_target',
        'Patient days to collect',
        'days',
      ),
      metric(
        'insurance_days_to_collect_target',
        'Insurance days to collect',
        'days',
      ),
    ],
  },
  {
    id: 'accounts-receivable',
    title: 'Accounts receivable',
    metrics: [
      metric('ar_aging_balance_goal', 'AR aging balance', 'currency'),
      metric('ar_over_90_balance_goal', 'AR over 90 days balance', 'currency'),
      metric('outstanding_balance_goal', 'Outstanding balance', 'currency'),
    ],
  },
  {
    id: 'provider-performance',
    title: 'Provider performance',
    metrics: [
      metric('dentist_production_goal', 'Dentist production', 'currency'),
      metric('hygienist_production_goal', 'Hygienist production', 'currency'),
      metric(
        'dentist_production_share_goal_pct',
        'Dentist production share',
        'percent',
      ),
      metric(
        'hygienist_production_share_goal_pct',
        'Hygienist production share',
        'percent',
      ),
      metric(
        'production_per_provider_goal',
        'Production per provider',
        'currency',
      ),
      metric(
        'production_by_procedure_goal',
        'Production by procedure',
        'currency',
      ),
      metric('patients_seen_goal', 'Patients seen', 'int'),
      metric('chair_utilization_goal_pct', 'Chair utilization', 'percent'),
    ],
  },
  {
    id: 'hygiene-performance',
    title: 'Hygiene performance',
    metrics: [
      metric(
        'net_hygiene_production_goal',
        'Net hygiene production',
        'currency',
      ),
      metric(
        'production_per_hygiene_visit_goal',
        'Production per hygiene visit',
        'currency',
      ),
    ],
  },
  {
    id: 'treatment-value',
    title: 'Treatment value',
    metrics: [
      metric(
        'treatment_value_billed_goal',
        'Treatment value billed',
        'currency',
      ),
      metric(
        'treatment_value_collected_goal',
        'Treatment value collected',
        'currency',
      ),
      metric(
        'treatment_value_potential_goal',
        'Treatment value potential',
        'currency',
      ),
    ],
  },
  {
    id: 'insurance-claims',
    title: 'Insurance claims',
    metrics: [
      metric('claims_under_review_goal', 'Claims under review', 'currency'),
      metric('claims_pending_goal', 'Claims pending', 'currency'),
      metric('claims_paid_goal', 'Claims paid', 'currency'),
      metric('claims_denied_goal', 'Claims denied', 'currency'),
    ],
  },
]

export const ALL_GOAL_FIELDS: ClinicGoalField[] = GOALS_SECTIONS.flatMap(
  (section) => section.metrics.map((m) => m.field),
)

const FIELD_FORMATS: Record<ClinicGoalField, GoalFormat> =
  GOALS_SECTIONS.reduce(
    (acc, section) => {
      section.metrics.forEach((m) => {
        acc[m.field] = m.format
      })
      return acc
    },
    {} as Record<ClinicGoalField, GoalFormat>,
  )

export const GOAL_PREFIX: Partial<Record<GoalFormat, string>> = {
  currency: '$',
  percent: '%',
}

export const GOAL_SUFFIX: Partial<Record<GoalFormat, string>> = {
  days: 'days',
}

/** Format the numeric part of a goal; the unit is rendered separately. */
export const formatGoalValue = (raw: string, format: GoalFormat): string => {
  if (raw == null || raw.trim() === '') return '-'
  const value = Number(raw)
  if (!Number.isFinite(value)) return '-'
  switch (format) {
    case 'currency':
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case 'days':
      return String(Math.round(value))
    case 'percent':
    case 'int':
    default:
      return value.toLocaleString('en-US')
  }
}

export type GoalCondition = 'at-least' | 'at-most'

export const GOAL_CONDITION_OPTIONS: { label: string; value: GoalCondition }[] =
  [
    { label: 'At least', value: 'at-least' },
    { label: 'At most', value: 'at-most' },
  ]

/** Build the editable string map keyed by model field from a fetched clinic goal. */
export const buildGoalsFromClinicGoal = (
  goal: ClinicGoal | null,
): Record<string, string> => {
  const goals: Record<string, string> = {}
  ALL_GOAL_FIELDS.forEach((field) => {
    const value = goal ? goal[field] : null
    goals[field] = value == null ? '' : String(value)
  })
  return goals
}

/**
 * Convert the editable string map into a numeric payload for the API.
 * Blank entries are skipped so existing values / nulls are preserved on update.
 */
export const buildGoalPayload = (
  goals: Record<string, string>,
): Record<string, number> => {
  const payload: Record<string, number> = {}
  ALL_GOAL_FIELDS.forEach((field) => {
    const raw = goals[field]
    if (raw == null || raw.trim() === '') return
    const value = Number(raw)
    if (Number.isFinite(value) && value >= 0) {
      const format = FIELD_FORMATS[field]
      payload[field] =
        format === 'int' || format === 'days' ? Math.round(value) : value
    }
  })
  return payload
}
